import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import { baseReducer } from '../core/base-reducer';
import type { ScanMode } from '../../pages/pick-pack-station/main/modes';
import { SCAN_MODES } from '../../pages/pick-pack-station/main/modes';
import { CSW_CREATE_LABEL, CSW_CREATE_LABEL_ABORTED, CSW_CREATE_LABEL_SUCCESS, CSW_CREATE_LABEL_FAILURE } from '../csw/csw.actions';
import {
  LABEL_SUCCESSFULLY_CREATED_FOR_SCAN_TO_PRINT,
  START_PURCHASE_AND_PRINT_JOB,
} from '../scan-to-print/s2p-purchase-and-print.actions';
import type { PickPackStationSerialCapture } from './pick-pack-station-scan.actions';
import * as Actions from './pick-pack-station-scan.actions';
import { namespace } from './pick-pack-station-scan.reducer.metadata';

export type PickPackStationScanQueue = {
  fulfillmentPlanIds: string[];
  currentIndex: number;
  batchName: string;
  isActive: boolean;
};

export type PickPackStationScanState = {
  activeMode: ScanMode;
  fulfillmentPlanId: string | null;
  isLoading: boolean;
  hasError: boolean;
  isPurchasing: boolean;
  isOrderSearch: boolean;
  searchValue: string;
  orderSearchResults: unknown[];
  verifiedItemCounts: Record<string, number>;
  isVerified: boolean;
  isSubmittingVerification: boolean;
  isReportingIssue: boolean;
  isAutoConfirmArmed: boolean;
  hasVerificationError: boolean;
  hasEditedVerification: boolean;
  lastScanResult: Actions.LastScanResult | null;
  queue: PickPackStationScanQueue;
  serialCapture: PickPackStationSerialCapture | null;
  isSerialCaptureEnabled: boolean;
};

export const emptyQueue: PickPackStationScanQueue = Object.freeze({
  fulfillmentPlanIds: emptyArray as string[],
  currentIndex: 0,
  batchName: '',
  isActive: false,
});

const initialState: PickPackStationScanState = {
  activeMode: SCAN_MODES.verifyAndPrint,
  fulfillmentPlanId: null,
  isLoading: false,
  hasError: false,
  isPurchasing: false,
  isOrderSearch: false,
  searchValue: '',
  orderSearchResults: emptyArray as unknown[],
  verifiedItemCounts: emptyObject as Record<string, number>,
  isVerified: false,
  isSubmittingVerification: false,
  isReportingIssue: false,
  isAutoConfirmArmed: true,
  hasVerificationError: false,
  hasEditedVerification: false,
  lastScanResult: null,
  queue: emptyQueue,
  serialCapture: null,
  isSerialCaptureEnabled: false,
};

const actionHandlerMap = {
  [Actions.SET_PICK_PACK_STATION_SCAN_MODE]: (state: PickPackStationScanState, { activeMode }: { activeMode: ScanMode }) => ({
    ...state,
    activeMode,
  }),
  [Actions.SET_PICK_PACK_STATION_FULFILLMENT_PLAN_ID]: (
    state: PickPackStationScanState,
    { fulfillmentPlanId }: { fulfillmentPlanId: string | null },
  ) => ({
    ...state,
    fulfillmentPlanId,
    isLoading: false,
    isPurchasing: false,
    isOrderSearch: false,
    orderSearchResults: emptyArray,
    searchValue: '',
    verifiedItemCounts: emptyObject as Record<string, number>,
    isVerified: false,
    isSubmittingVerification: false,
    isAutoConfirmArmed: true,
    hasVerificationError: false,
    hasEditedVerification: false,
    lastScanResult: null,
    serialCapture: null,
  }),
  [Actions.SEARCH_PICK_PACK_STATION_ORDER]: (state: PickPackStationScanState, { searchValue }: { searchValue: string }) => ({
    ...state,
    isLoading: true,
    hasError: false,
    isOrderSearch: false,
    orderSearchResults: emptyArray,
    searchValue,
  }),
  [Actions.PICK_PACK_STATION_SELECT_FULFILLMENT_PLAN]: (state: PickPackStationScanState) => ({
    ...state,
    isLoading: true,
    hasError: false,
  }),
  [Actions.PICK_PACK_STATION_ORDER_SEARCH_RESULTS]: (state: PickPackStationScanState, { orders }: { orders: unknown[] }) => ({
    ...state,
    isLoading: false,
    isOrderSearch: true,
    orderSearchResults: orders,
  }),
  [Actions.PICK_PACK_STATION_ORDER_SEARCH_ERROR]: (state: PickPackStationScanState) => ({
    ...state,
    fulfillmentPlanId: null,
    isLoading: false,
    hasError: true,
    isOrderSearch: false,
    orderSearchResults: emptyArray,
  }),
  [Actions.SET_PICK_PACK_STATION_SEARCH_INPUT]: (state: PickPackStationScanState, { searchValue }: { searchValue: string }) => ({
    ...state,
    searchValue,
  }),
  [Actions.CLEAR_PICK_PACK_STATION_ORDER_SEARCH]: (state: PickPackStationScanState) => ({
    ...state,
    isLoading: false,
    hasError: false,
    isOrderSearch: false,
    orderSearchResults: emptyArray,
    searchValue: '',
  }),
  [Actions.RESET_PICK_PACK_STATION_ORDER]: (state: PickPackStationScanState) => ({
    ...state,
    fulfillmentPlanId: null,
    isLoading: false,
    hasError: false,
    isPurchasing: false,
    isOrderSearch: false,
    orderSearchResults: emptyArray,
    searchValue: '',
    verifiedItemCounts: emptyObject as Record<string, number>,
    isVerified: false,
    isSubmittingVerification: false,
    isAutoConfirmArmed: true,
    hasVerificationError: false,
    hasEditedVerification: false,
    lastScanResult: null,
    serialCapture: null,
  }),
  [Actions.VERIFY_PICK_PACK_STATION_ITEM]: (
    state: PickPackStationScanState,
    { itemId, verifiedCount }: { itemId: string; verifiedCount: number },
  ) => ({
    ...state,
    verifiedItemCounts: { ...state.verifiedItemCounts, [itemId]: verifiedCount },
    isAutoConfirmArmed: true,
    hasEditedVerification: false,
    hasVerificationError: false,
  }),
  [Actions.CLEAR_PICK_PACK_STATION_ITEM]: (state: PickPackStationScanState, { itemId }: { itemId: string }) => ({
    ...state,
    verifiedItemCounts: { ...state.verifiedItemCounts, [itemId]: 0 },
    isAutoConfirmArmed: true,
    hasVerificationError: false,
  }),
  [Actions.VERIFY_ALL_PICK_PACK_STATION_ITEMS]: (
    state: PickPackStationScanState,
    { items }: { items: ReadonlyArray<{ itemId: string; quantity: number }> },
  ) => ({
    ...state,
    verifiedItemCounts: Object.fromEntries(items.map(({ itemId, quantity }) => [itemId, quantity])),
    isAutoConfirmArmed: true,
    hasEditedVerification: false,
    hasVerificationError: false,
    serialCapture: null,
  }),
  [Actions.CLEAR_ALL_PICK_PACK_STATION_ITEMS]: (state: PickPackStationScanState) => ({
    ...state,
    verifiedItemCounts: emptyObject as Record<string, number>,
    isAutoConfirmArmed: true,
    hasVerificationError: false,
    serialCapture: null,
  }),
  [Actions.SUBMIT_PICK_PACK_STATION_VERIFICATION]: (state: PickPackStationScanState) => ({
    ...state,
    isSubmittingVerification: true,
    isAutoConfirmArmed: false,
    hasVerificationError: false,
  }),
  [Actions.SUBMIT_PICK_PACK_STATION_VERIFICATION_SUCCESS]: (
    state: PickPackStationScanState,
    { fulfillmentPlanId }: { fulfillmentPlanId: string },
  ) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId) return state;
    return {
      ...state,
      isVerified: true,
      isSubmittingVerification: false,
      hasEditedVerification: false,
    };
  },
  [Actions.SUBMIT_PICK_PACK_STATION_VERIFICATION_ERROR]: (
    state: PickPackStationScanState,
    { fulfillmentPlanId }: { fulfillmentPlanId: string },
  ) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId) return state;
    return {
      ...state,
      isSubmittingVerification: false,
      hasVerificationError: true,
    };
  },
  [Actions.INITIALIZE_PICK_PACK_STATION_SCAN_QUEUE]: (
    state: PickPackStationScanState,
    { fulfillmentPlanIds, batchName }: { fulfillmentPlanIds: string[]; batchName: string },
  ) => ({
    ...state,
    queue: { fulfillmentPlanIds, currentIndex: 0, batchName, isActive: true },
    fulfillmentPlanId: fulfillmentPlanIds[0] ?? null,
  }),
  [Actions.ADVANCE_PICK_PACK_STATION_QUEUE]: (state: PickPackStationScanState) => {
    const { queue } = state;
    if (!queue.isActive) return state;
    const nextIndex = queue.currentIndex + 1;
    return {
      ...state,
      queue: { ...queue, currentIndex: nextIndex },
      fulfillmentPlanId: queue.fulfillmentPlanIds[nextIndex] ?? null,
    };
  },
  [Actions.CLEAR_PICK_PACK_STATION_SCAN_QUEUE]: (state: PickPackStationScanState) => ({
    ...state,
    queue: emptyQueue,
  }),
  [Actions.EDIT_PICK_PACK_STATION_VERIFICATION]: (
    state: PickPackStationScanState,
    { items }: { items: ReadonlyArray<{ itemId: string; quantity: number }> },
  ) => ({
    ...state,
    isVerified: false,
    isAutoConfirmArmed: false,
    hasVerificationError: false,
    hasEditedVerification: true,
    verifiedItemCounts: Object.fromEntries(items.map(({ itemId, quantity }) => [itemId, quantity])),
  }),
  [Actions.SET_PICK_PACK_STATION_LAST_SCAN_RESULT]: (
    state: PickPackStationScanState,
    { kind, scannedValue, itemId }: Pick<Actions.LastScanResult, 'kind' | 'scannedValue' | 'itemId'>,
  ) => ({
    ...state,
    lastScanResult: {
      sequence: (state.lastScanResult?.sequence ?? 0) + 1,
      kind,
      scannedValue,
      itemId,
    },
  }),
  [Actions.REPORT_PICK_PACK_STATION_ISSUE]: (state: PickPackStationScanState) => ({ ...state, isReportingIssue: true }),
  [Actions.REPORT_PICK_PACK_STATION_ISSUE_SUCCESS]: (state: PickPackStationScanState) => ({ ...state, isReportingIssue: false }),
  [Actions.REPORT_PICK_PACK_STATION_ISSUE_ERROR]: (state: PickPackStationScanState) => ({ ...state, isReportingIssue: false }),
  [Actions.REQUEST_PICK_PACK_STATION_SERIAL_CAPTURE]: (
    state: PickPackStationScanState,
    {
      itemId,
      pendingVerifiedCount,
      isManualVerify,
      scannedValue,
      willBeAllVerified,
    }: {
      itemId: string;
      pendingVerifiedCount: number;
      isManualVerify: boolean;
      scannedValue: string | null;
      willBeAllVerified: boolean;
    },
  ) => ({
    ...state,
    serialCapture: {
      itemId,
      pendingVerifiedCount,
      editUnitIndex: null,
      isManualVerify,
      scannedValue,
      willBeAllVerified,
    },
  }),
  [Actions.EDIT_PICK_PACK_STATION_SERIAL_NUMBER]: (
    state: PickPackStationScanState,
    { itemId, unitIndex }: { itemId: string; unitIndex: number },
  ) => ({
    ...state,
    serialCapture: {
      itemId,
      pendingVerifiedCount: null,
      editUnitIndex: unitIndex,
      isManualVerify: false,
      scannedValue: null,
      willBeAllVerified: false,
    },
  }),
  [Actions.ADVANCE_PICK_PACK_STATION_SERIAL_CAPTURE_UNIT]: (state: PickPackStationScanState) => {
    const { serialCapture } = state;
    if (serialCapture === null || serialCapture.pendingVerifiedCount === null) return state;
    return {
      ...state,
      serialCapture: {
        ...serialCapture,
        pendingVerifiedCount: serialCapture.pendingVerifiedCount + 1,
      },
    };
  },
  [Actions.CLEAR_PICK_PACK_STATION_SERIAL_CAPTURE]: (state: PickPackStationScanState) => {
    if (state.serialCapture === null) return state;
    return { ...state, serialCapture: null };
  },
  [Actions.SET_PICK_PACK_STATION_SERIAL_CAPTURE_ENABLED]: (state: PickPackStationScanState, { isEnabled }: { isEnabled: boolean }) => ({
    ...state,
    isSerialCaptureEnabled: isEnabled,
  }),
  [CSW_CREATE_LABEL]: (state: PickPackStationScanState, { fulfillmentPlanId }: { fulfillmentPlanId: string }) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId) return state;
    return { ...state, isPurchasing: true };
  },
  [CSW_CREATE_LABEL_SUCCESS]: (state: PickPackStationScanState, { fulfillmentPlanId }: { fulfillmentPlanId: string }) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId) return state;
    return { ...state, isPurchasing: false };
  },
  // Any failure for the active plan is terminal for the station — the s2p pipeline's error path
  // dispatches isLabelPurchaseComplete=false (no retry follows it), and the direct CSW path
  // (bypass) only ever dispatches true. Cost-review retries cannot happen for a station plan.
  [CSW_CREATE_LABEL_FAILURE]: (state: PickPackStationScanState, { fulfillmentPlanId }: { fulfillmentPlanId?: string }) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId) return state;
    return { ...state, isPurchasing: false };
  },
  // A declined purchase gate (UPS-invoiced modal, Shipsurance terms) never reaches the API —
  // release the purchasing lock so the Print CTA recovers without a re-scan.
  [CSW_CREATE_LABEL_ABORTED]: (state: PickPackStationScanState, { fulfillmentPlanId }: { fulfillmentPlanId?: string }) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId) return state;
    return { ...state, isPurchasing: false };
  },
  // The no-Connect download path purchases through the legacy s2p pipeline, which never
  // dispatches CSW_CREATE_LABEL — lock on its job start and release on its terminal signals,
  // mirroring the legacy scan panel lock.
  [START_PURCHASE_AND_PRINT_JOB]: (state: PickPackStationScanState, { fulfillmentPlanId }: { fulfillmentPlanId?: string }) => {
    if (fulfillmentPlanId !== state.fulfillmentPlanId) return state;
    return { ...state, isPurchasing: true };
  },
  // Unconditional on purpose: the legacy s2p pipeline action carries no fulfillmentPlanId, and
  // only one purchase can be in flight at a time — revisit if that invariant ever changes.
  [LABEL_SUCCESSFULLY_CREATED_FOR_SCAN_TO_PRINT]: (state: PickPackStationScanState) => ({
    ...state,
    isPurchasing: false,
  }),
};

export const ConnectedActionHandlerMap = baseReducer(initialState, actionHandlerMap);
export { namespace };
